/**
 * "Ma taille": keeps only one size's numbers in a multi-size pattern, client-side.
 *   `72 (78, 84)` → `78` · `k1 (1, 2, 1)` → `k2` · `78-84-90` → `84` (DROPS) · `4 : 6 : 8` → `6`
 * A group is rewritten only when it has exactly one value per size, so ranges (`Rows 3-60`) and
 * asides (`(RS)`, `(4")`) are never touched. Display only: the engine still reads the full text.
 */

const NUM = String.raw`(?:\d+(?:[.,]\d+)?[½¼¾]?|[½¼¾])`;
const VAL = String.raw`(?:${NUM}|[-–—])`;
const PAREN = new RegExp(String.raw`(?<![\p{L}\d])([A-Za-z]*)(${NUM})\s*\(\s*(${VAL}(?:\s*[,;]\s*${VAL})*)\s*\)`, 'gu');
const DROPS = /(?<![\d.,-])(\d+(?:-\d+){2,})(?![\d-])/g;
const COLON = /(?<![\d.,])(\d+(?:\s:\s\d+){2,})(?![\d])/g;

export interface ReaderSegment {
  text: string;
  /** true when this is the chosen size's value taken from a multi-size group. */
  picked?: boolean;
  /** The original group, for a tooltip ("72 (78, 84)"). */
  from?: string;
}

interface Hit {
  start: number;
  end: number;
  text: string;
  from: string;
}

function hits(line: string, size: number, count: number): Hit[] {
  const found: Hit[] = [];
  const add = (start: number, end: number, value: string | undefined, prefix = '') => {
    if (value === undefined) return;
    const v = /^[-–—]$/.test(value) ? '—' : value;
    found.push({ start, end, text: v === '—' ? v : prefix + v, from: line.slice(start, end) });
  };
  for (const m of line.matchAll(PAREN)) {
    const values = [m[2], ...m[3].split(/\s*[,;]\s*/)];
    if (values.length === count) add(m.index, m.index + m[0].length, values[size], m[1]);
  }
  for (const m of line.matchAll(DROPS)) {
    const values = m[1].split('-');
    if (values.length === count) add(m.index, m.index + m[0].length, values[size]);
  }
  for (const m of line.matchAll(COLON)) {
    const values = m[1].split(/\s:\s/);
    if (values.length === count) add(m.index, m.index + m[0].length, values[size]);
  }
  found.sort((a, b) => a.start - b.start);
  const out: Hit[] = [];
  for (const h of found) if (!out.length || h.start >= out[out.length - 1].end) out.push(h);
  return out;
}

/** One line with only size `size` (0-based) out of `count` sizes. */
export function rewriteLineForSize(line: string, size: number, count: number): ReaderSegment[] {
  if (count < 2 || size < 0 || size >= count) return line ? [{ text: line }] : [];
  const segments: ReaderSegment[] = [];
  let at = 0;
  for (const h of hits(line, size, count)) {
    if (h.start > at) segments.push({ text: line.slice(at, h.start) });
    segments.push({ text: h.text, picked: true, from: h.from });
    at = h.end;
  }
  if (at < line.length) segments.push({ text: line.slice(at) });
  return segments;
}

/** Whole text with only one size's numbers (plain string). */
export function rewriteForSize(text: string, size: number, count: number): string {
  return text
    .split('\n')
    .map((line) => rewriteLineForSize(line, size, count).map((s) => s.text).join(''))
    .join('\n');
}
